// Property Form Validation Utilities - Extracted from PropertyForm.js

// List of blocked/inappropriate words for content moderation
export const BLOCKED_WORDS = [
  // Strong profanity (English)
  'fuck', 'fucking', 'fucked', 'fucker', 'fucks', 'fck', 'f*ck', 'fu*k',
  'shit', 'shitting', 'shitted', 'bullshit', 'sh*t', 'sh1t',
  'ass', 'asshole', 'arse', 'arsehole', 'a**hole', 'a$$',
  'bitch', 'bitches', 'b*tch', 'b1tch',
  'bastard', 'bastards', 'b*stard',
  'damn', 'damned', 'goddamn',
  'cunt', 'c*nt', 'cunts',
  'dick', 'dicks', 'd*ck', 'dickhead',
  'cock', 'cocks', 'c*ck',
  'pussy', 'pussies', 'p*ssy',
  'whore', 'whores', 'wh*re',
  'slut', 'sluts', 'sl*t',
  'piss', 'pissed', 'pissing',
  'crap', 'crappy',
  'screw', 'screwed',
  
  // Abusive/insulting words
  'idiot', 'idiots', 'idiotic',
  'stupid', 'stupidity',
  'fool', 'foolish', 'fools',
  'moron', 'morons', 'moronic',
  'jerk', 'jerks',
  'dumb', 'dumbass', 'dumbasses',
  'loser', 'losers',
  'retard', 'retarded', 'retards',
  'freak', 'freaks',
  'psycho', 'psychos',
  'creep', 'creepy', 'creeps',
  'pervert', 'perverts', 'perv',
  'weirdo', 'weirdos',
  'nutcase', 'nutjob',
  'scum', 'scumbag', 'scumbags',
  'trash', 'white trash',
  'garbage',
  'pig', 'pigs',
  'dog', 'dogs', // when used as insult
  'animal', 'animals', // when used as insult
  
  // Threatening words
  'kill', 'killed', 'killer', 'killing', 'kills',
  'murder', 'murdered', 'murderer', 'murdering',
  'threat', 'threaten', 'threatened', 'threatening',
  'attack', 'attacked', 'attacking', 'attacks',
  'bomb', 'bombs', 'bombing', 'bomber',
  'terrorist', 'terrorism', 'terrorists',
  'violence', 'violent',
  'destroy', 'destroyed', 'destroying',
  'die', 'dies', 'dying', 'death', 'dead',
  'harm', 'harmed', 'harming',
  'hurt', 'hurting',
  'stab', 'stabbed', 'stabbing',
  'shoot', 'shooting', 'shot',
  'gun', 'guns',
  'weapon', 'weapons',
  'revenge',
  'assault', 'assaulted',
  
  // Discriminatory words
  'racist', 'racism', 'racists',
  'sexist', 'sexism',
  'hate', 'hated', 'hater', 'haters', 'hatred', 'hating',
  'discrimination', 'discriminate',
  'bigot', 'bigots', 'bigotry',
  'nazi', 'nazis',
  'fascist', 'fascists',
  
  // Spam/scam indicators
  'free money', 'lottery', 'prize winner', 'nigerian prince', 'get rich quick',
  'click here', 'act now', 'limited time', 'winner selected',
  'congratulations you won', 'you have been selected',
  
  // Inappropriate/adult content
  'porn', 'porno', 'pornography', 'p*rn',
  'xxx', 'x-rated', 'xrated',
  'nude', 'nudes', 'nudity', 'naked',
  'sex', 'sexual', 'sexually', 's*x',
  'erotic', 'erotica',
  'adult content', 'adult only', '18+', 'nsfw',
  'explicit',
  'boobs', 'boob', 'tits', 'tit',
  'genitals', 'genital',
  'orgasm', 'orgasms',
  'masturbate', 'masturbation',
  'prostitute', 'prostitution', 'escort service',
  
  // Hindi/Indian abusive words (transliterated)
  'gandu', 'gaandu',
  'chutiya', 'chutiye', 'chutia', 'ch*tiya',
  'madarchod', 'motherchod', 'mc', 'm*darchod',
  'behenchod', 'behen chod', 'bc', 'b*henchod',
  'bhosdike', 'bhosdi', 'bhosdiwale',
  'harami', 'haramkhor', 'haraamkhor',
  'saala', 'saale', 'sala',
  'kamina', 'kamine', 'kamini',
  'randi', 'raand', 'r*ndi',
  'kutiya', 'kutti', 'kuttiya',
  'chod', 'chuda', 'chudi',
  'lund', 'lauda', 'lawda', 'l*nd',
  'gaand', 'gand', 'g**nd',
  'bhenchod',
  'tatti', 'potty',
  'ullu', 'ullu ka pattha',
  'gadha', 'gadhe',
  'kutta', 'kutte',
  'suar', 'suwar',
  'hijra', 'hijda', 'chakka',
  'dalal', 'dalla',
  'namard', 'napunsak',
  
  // Other regional abusive words
  'benchod', 'banchod',
  'teri maa', 'tera baap',
  'maderchod',
  'laudu', 'lodu',
  'jhant', 'jhaat',
  'bhadwa', 'bhadwe',
  'chinaal', 'chinal',
  'pataka', 'item' // when used inappropriately
];

/**
 * Validate text content for inappropriate language
 * @param {string} text - Text to validate
 * @param {string} fieldName - Name of the field for error message
 * @returns {{ isValid: boolean, error?: string }}
 */
export const validateTextContent = (text, fieldName) => {
  if (!text) return { isValid: true };
  
  const lowerText = text.toLowerCase();
  
  for (const word of BLOCKED_WORDS) {
    if (lowerText.includes(word)) {
      return {
        isValid: false,
        error: `${fieldName} contains inappropriate or banned content. Please use professional language.`
      };
    }
  }
  
  // Check for excessive special characters (spam indicator)
  const specialCharCount = (text.match(/[!@#$%^&*()_+=\[\]{};':"\\|,.<>\/?]/g) || []).length;
  if (specialCharCount > text.length * 0.3) {
    return {
      isValid: false,
      error: `${fieldName} contains too many special characters. Please provide valid content.`
    };
  }
  
  // Check for all caps (shouting/spam)
  if (text.length > 10 && text === text.toUpperCase() && /[A-Z]/.test(text)) {
    return {
      isValid: false,
      error: `${fieldName} should not be in all capital letters. Please use proper formatting.`
    };
  }
  
  return { isValid: true };
};

/**
 * Validate numeric fields
 * @param {any} value - Value to validate
 * @param {string} fieldName - Name of the field for error message
 * @param {object} options - Validation options
 * @returns {{ isValid: boolean, error?: string }}
 */
export const validateNumericField = (value, fieldName, options = {}) => {
  const { min = 0, max = Infinity, required = false, allowDecimal = true } = options;
  
  // Skip validation if empty and not required
  if (value === '' || value === null || value === undefined) {
    if (required) {
      return { isValid: false, error: `${fieldName} is required` };
    }
    return { isValid: true };
  }
  
  // Convert to string for validation
  const strValue = String(value).trim();
  
  // Check if it contains only valid numeric characters
  const numericPattern = allowDecimal ? /^-?\d*\.?\d+$/ : /^-?\d+$/;
  if (!numericPattern.test(strValue)) {
    return {
      isValid: false,
      error: `${fieldName} must be a valid number (no letters or special characters allowed)`
    };
  }
  
  const numValue = parseFloat(strValue);
  
  if (isNaN(numValue)) {
    return {
      isValid: false,
      error: `${fieldName} must be a valid number`
    };
  }
  
  if (numValue < min) {
    return {
      isValid: false,
      error: `${fieldName} must be at least ${min}`
    };
  }
  
  if (numValue > max) {
    return {
      isValid: false,
      error: `${fieldName} cannot exceed ${max}`
    };
  }
  
  return { isValid: true };
};

/**
 * Prevent non-numeric input in number fields
 * @param {KeyboardEvent} e - Keyboard event
 */
export const handleNumericKeyDown = (e) => {
  // Allow: backspace, delete, tab, escape, enter, decimal point, minus
  if (
    [8, 9, 13, 27, 46, 110, 190].includes(e.keyCode) || // Special keys
    (e.keyCode === 189 || e.keyCode === 109) || // Minus sign
    (e.keyCode >= 35 && e.keyCode <= 40) || // Home, End, arrows
    (e.ctrlKey && [65, 67, 86, 88].includes(e.keyCode)) // Ctrl+A, C, V, X
  ) {
    return;
  }
  // Allow: numbers (0-9) from main keyboard and numpad
  if (
    (e.keyCode >= 48 && e.keyCode <= 57) || // 0-9 main keyboard
    (e.keyCode >= 96 && e.keyCode <= 105) // 0-9 numpad
  ) {
    return;
  }
  // Block everything else
  e.preventDefault();
};

/**
 * Handle numeric input paste - strip non-numeric characters
 * @param {ClipboardEvent} e - Clipboard event
 */
export const handleNumericPaste = (e) => {
  e.preventDefault();
  const pastedText = e.clipboardData.getData('text');
  const numericOnly = pastedText.replace(/[^0-9.-]/g, '');
  const input = e.target;
  const start = input.selectionStart;
  const end = input.selectionEnd;
  const currentValue = input.value;
  const newValue = currentValue.substring(0, start) + numericOnly + currentValue.substring(end);
  
  // Update the input value
  input.value = newValue;
  
  // Trigger change event
  const changeEvent = new Event('input', { bubbles: true });
  input.dispatchEvent(changeEvent);
};

/**
 * Validate YouTube URL format
 * @param {string} url - YouTube URL to validate
 * @returns {boolean}
 */
export const isValidYoutubeUrl = (url) => {
  if (!url) return false;
  const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|v\/)|youtu\.be\/)[\w-]+/;
  return youtubeRegex.test(url);
};

/**
 * Extract YouTube video ID from URL
 * @param {string} url - YouTube URL
 * @returns {string|null}
 */
export const extractYoutubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);
  return match ? match[1] : null;
};

/**
 * Text fields that require content validation
 */
export const TEXT_FIELDS_TO_VALIDATE = [
  { field: 'title', label: 'Property Title' },
  { field: 'description', label: 'Description' },
  { field: 'location', label: 'Location' },
  { field: 'locality', label: 'Locality' },
  { field: 'project_name', label: 'Project Name' },
  { field: 'builder_name', label: 'Builder Name' },
  { field: 'furnishing_details', label: 'Furnishing Details' }
];

/**
 * Numeric fields with their validation options
 */
export const NUMERIC_FIELDS_TO_VALIDATE = [
  { field: 'area', label: 'Carpet Area', options: { min: 1, max: 100000000, required: true } },
  { field: 'built_up_area', label: 'Built-up Area', options: { min: 1, max: 100000000 } },
  { field: 'super_built_up_area', label: 'Super Built-up Area', options: { min: 1, max: 100000000 } },
  { field: 'all_inclusive_price', label: 'All-Inclusive Price', options: { min: 1, max: 100000000000 } },
  { field: 'agreement_value', label: 'Agreement Value', options: { min: 0, max: 100000000000 } },
  { field: 'monthly_rent', label: 'Monthly Rent', options: { min: 0, max: 100000000 } },
  { field: 'security_deposit', label: 'Security Deposit', options: { min: 0, max: 100000000 } },
  { field: 'maintenance_charges', label: 'Maintenance Charges', options: { min: 0, max: 10000000 } },
  { field: 'corpus_fund', label: 'Corpus Fund', options: { min: 0, max: 100000000 } },
  { field: 'infrastructure_cost', label: 'Infrastructure Cost', options: { min: 0, max: 100000000 } },
  { field: 'booking_amount', label: 'Booking Amount', options: { min: 0, max: 100000000 } },
  { field: 'stamp_duty_percent', label: 'Stamp Duty %', options: { min: 0, max: 100 } },
  { field: 'gst_percent', label: 'GST %', options: { min: 0, max: 100 } },
  { field: 'rent_escalation', label: 'Rent Escalation %', options: { min: 0, max: 100 } },
  { field: 'bedrooms', label: 'Bedrooms', options: { min: 0, max: 50, allowDecimal: false } },
  { field: 'bathrooms', label: 'Bathrooms', options: { min: 0, max: 50, allowDecimal: false } },
  { field: 'balconies', label: 'Balconies', options: { min: 0, max: 20, allowDecimal: false } },
  { field: 'parking', label: 'Parking Spaces', options: { min: 0, max: 100, allowDecimal: false } },
  { field: 'floor_number', label: 'Floor Number', options: { min: -5, max: 200, allowDecimal: false } },
  { field: 'total_floors', label: 'Total Floors', options: { min: 1, max: 200, allowDecimal: false } },
  { field: 'existing_loan_amount', label: 'Existing Loan Amount', options: { min: 0, max: 100000000000 } },
  { field: 'rent_per_sqft_carpet', label: 'Rent per Sqft (Carpet)', options: { min: 0, max: 100000 } },
  { field: 'rent_per_sqft_builtup', label: 'Rent per Sqft (Built-up)', options: { min: 0, max: 100000 } },
  { field: 'number_of_lifts', label: 'Number of Lifts', options: { min: 0, max: 50, allowDecimal: false } },
  { field: 'total_rooms', label: 'Total Rooms', options: { min: 0, max: 1000, allowDecimal: false } },
  { field: 'total_beds', label: 'Total Beds', options: { min: 0, max: 5000, allowDecimal: false } },
  { field: 'floor_plate_area', label: 'Floor Plate Area', options: { min: 0, max: 10000000 } },
  { field: 'total_carpet_area', label: 'Total Carpet Area', options: { min: 0, max: 100000000 } },
  { field: 'total_saleable_area', label: 'Total Saleable Area', options: { min: 0, max: 100000000 } }
];

/**
 * Required fields for form validation - Base fields only
 * Price field (all_inclusive_price vs monthly_rent) is determined dynamically based on purpose
 */
export const REQUIRED_FIELDS = [
  { field: 'title', label: 'Property Title', tab: 'basic' },
  { field: 'description', label: 'Description', tab: 'basic' },
  { field: 'location', label: 'Location', tab: 'basic' },
  { field: 'area', label: 'Carpet Area', tab: 'basic' }
];

/**
 * Tab-specific validation fields
 * Note: Price field validation is handled dynamically in PropertyForm.js based on purpose
 */
export const TAB_VALIDATIONS = {
  basic: [
    { field: 'title', label: 'Property Title' },
    { field: 'description', label: 'Description' },
    { field: 'property_subtype', label: 'Property Category' },
    { field: 'area', label: 'Carpet Area' },
    { field: 'location', label: 'Location' }
  ],
  specifications: [],
  legal: []
};
